import React, {HTMLAttributes, ReactNode} from "react";
import {FormSetup} from "../models/definitions/FormSetup";
import {QuestionSetup} from "../models/definitions/QuestionSetup";
import {DynamicFormsSetupEditorData} from "./DynamicFormsSetupEditorData";
import {
	Autocomplete,
	Checkbox,
	FormControl,
	IconButton,
	InputLabel,
	MenuItem,
	Select,
	Stack,
	TextField
} from "@mui/material";
import {getDefaultValue, getValue, nullableOf} from "./ValueFunctions";
import {QuestionValueOption} from "../models/definitions/QuestionValueOption";
import {Add, DragHandle, Remove} from "@mui/icons-material";
import {CodeTextField} from "../../../components/common/input/CodeTextField";
import {json, jsonParseLinter} from "@codemirror/lang-json";
import {DragAndDrop} from "../../../components/common/DragAndDrop";
import {translateFunction} from "../../../localization/TranslationService";

const translate = translateFunction("SureBasePortalDynamicForms");

export interface RowProps {
	readonly children: ReactNode;
}

export function Row(props: RowProps & HTMLAttributes<HTMLSpanElement>) {
	return <span
		{...props}
		style={{
			display: "flex",
			margin: "5px",
			alignItems: "start",
			...props.style
		}}
	>
		{props.children}
	</span>;
}

export function readonlyField<TObject extends FormSetup | QuestionSetup>(
	data: DynamicFormsSetupEditorData<TObject>,
	field: keyof TObject & string
) {
	return <Row key={field}>
		<Checkbox style={{visibility: "hidden"}}/>
		<TextField
			label={translate(field)}
			variant={"outlined"}
			disabled={true}
			value={data.setup[field] ?? ""}
			style={{flexGrow: 1}}
			InputLabelProps={{
				shrink: true, // avoid label to take up the text field space, when there is no value inside the text field
			}}
		/>
	</Row>;
}

export function simpleField<TObject extends FormSetup | QuestionSetup>(
	data: DynamicFormsSetupEditorData<TObject>,
	field: keyof TObject & string,
	updateState: (field: string, value: any) => void,
	{
		isWrapped = false,
		isMultiline = false,
		options,
		disabled = false,
	}: {
		isWrapped?: boolean;
		isMultiline?: boolean;
		options?: string[];
		disabled?: boolean;
	} = {}
) {
	const value: string | null = getValue(field, data, isWrapped);
	return <Row key={field}>
		{wrapperCheckbox(data, field, updateState, isWrapped, disabled)}
		{options
			? <Autocomplete
				disablePortal
				options={[...options]}
				value={value ?? null}
				autoSelect
				autoComplete
				style={{flexGrow: 1}}
				renderInput={(params) => <TextField
					{...params}
					label={translate(field)}
					variant={"outlined"}
					multiline={isMultiline}
				/>}
				onChange={(event, value) => {
					const newValue = isWrapped ? nullableOf(value) : value;
					updateState(field, newValue);
				}}
				disabled={disabled}
			/>
			: <TextField
				label={translate(field)}
				variant={"outlined"}
				value={value ?? ""}
				style={{flexGrow: 1}}
				onChange={event => {
					const newValue = isWrapped ? nullableOf(event.target.value) : event.target.value;
					updateState(field, newValue);
				}}
				multiline={isMultiline}
				disabled={disabled}
			/>}
	</Row>;
}

export function wrapperCheckbox<TObject extends FormSetup | QuestionSetup>(
	data: DynamicFormsSetupEditorData<TObject>,
	field: keyof TObject & string,
	updateState: (field: string, value: any) => void,
	isWrapped: boolean,
	disabled: boolean,
) {
	if (isWrapped)
		return <Checkbox
			checked={!!data.setup[field]}
			onChange={event => {
				if (event.target.checked) {
					updateState(field, getDefaultValue(field, data, true));
				} else {
					updateState(field, null);
				}
			}}
			disabled={disabled}
		/>;
	else
		return <Checkbox style={{visibility: "hidden"}}/>;
}

export interface DropdownOption {
	readonly key: string;
	readonly display: string;
}

export function optionsField<TObject extends FormSetup | QuestionSetup>(
	data: DynamicFormsSetupEditorData<TObject>,
	field: keyof TObject & string,
	updateState: (field: string, value: any) => void,
	options: DropdownOption[],
	{isWrapped = false, disabled = false}: { isWrapped?: boolean; disabled?: boolean } = {}
) {
	const setup = data.setup;
	const value = getValue(field, data, isWrapped);
	return <Row key={field}>
		{isWrapped
			? <Checkbox
				checked={!!setup[field]}
				onChange={event => {
					if (event.target.checked) {
						updateState(field, getDefaultValue(field, data, true));
					} else {
						updateState(field, null);
					}
				}}
				disabled={disabled}
			/>
			: <Checkbox style={{visibility: "hidden"}}/>}
		<FormControl
			style={{flexGrow: 1}}>
			<InputLabel
				id="user-settings-language-select-label">{translate(field)}</InputLabel>
			<Select
				labelId={"user-settings-language-select-label"}
				label={"valueType"}
				value={value ?? ""}
				style={{flexGrow: 1}}
				onChange={event => {
					const targetValue = event.target.value === "" ? null : event.target.value;
					const newValue = isWrapped ? nullableOf(targetValue) : targetValue;
					updateState(field, newValue);
				}}
				disabled={disabled}
			>
				<MenuItem key={""} value={""}>none</MenuItem>
				{options.map(it => <MenuItem key={it.key} value={it.key}>{it.display}</MenuItem>)}
			</Select>
		</FormControl>
	</Row>;
}

// assumed always wrapped
export function ValueOptionsField(
	data: DynamicFormsSetupEditorData<QuestionSetup>,
	field: keyof QuestionSetup,
	updateState: (field: string, value: any) => void,
	{
		disabled = false,
	}: {
		disabled?: boolean;
	}
) {
	const setup = data.setup;

	const items: QuestionValueOption[] = getValue(field, data, true, []);

	return <Row key={field}>
		<Checkbox
			checked={!!setup[field]}
			onChange={event => {
				if (event.target.checked) {
					updateState(field, getDefaultValue(field, data, true, []));
				} else {
					updateState(field, null);
				}
			}}
			disabled={disabled}
		/>
		<Stack style={{flexGrow: 1, alignItems: "stretch"}}>
			{translate(field)}

			<DragAndDrop items={items} disabled={disabled} onItemsChanged={newItems => {
				const newValue = nullableOf(newItems);
				updateState(field, newValue);
			}}>
				{(item, index) =>
					<Stack key={index.toString()} style={{width: "100%", margin: "4px 0"}} gap={1}>
						<IconButton
							color={"primary"}
							onClick={() => {
								const newItems = [...items];
								newItems.splice(index, 1);
								const newValue = nullableOf(newItems);
								updateState(field, newValue);
							}}
							disabled={disabled}
						>
							<Remove/>
						</IconButton>
						<TextField
							variant={"outlined"}
							value={item.value}
							label={"value"}
							onChange={event => {
								const newItems = [...items];
								newItems[index] = {...newItems[index], value: event.target.value};
								const newValue = nullableOf(newItems);
								updateState(field, newValue);
							}}
							disabled={disabled}
						/>
						<TextField
							variant={"outlined"}
							value={item.display}
							label={"display"}
							style={{flexGrow: 2}}
							onChange={event => {
								const newItems = [...items];
								newItems[index] = {...newItems[index], display: event.target.value};
								const newValue = nullableOf(newItems);
								updateState(field, newValue);
							}}
							disabled={disabled}
						/>
						<TextField
							variant={"outlined"}
							value={item.extraDisplay}
							label={"extra display"}
							onChange={event => {
								const newItems = [...items];
								const value = event.target.value;
								newItems[index] = {...newItems[index], extraDisplay: value ? value : null};
								const newValue = nullableOf(newItems);
								updateState(field, newValue);
							}}
							disabled={disabled}
						/>
						<CodeTextField
							maxHeight={"400px"}
							TextFieldProps={{
								label: "extra display expression",
								variant: "outlined",
								value: JSON.stringify(item.extraDisplayExpression , null, 2) ?? "",
								style: {flexGrow: 1},
								placeholder: "null",
								disabled: disabled,
								onChange: event => {
									try {
										const newItems = [...items];
										const newValueJson: string = (event as any).value;
										const newValueJsonNullable = JSON.parse(newValueJson);
										newItems[index] = {...newItems[index], extraDisplayExpression: newValueJsonNullable ? newValueJsonNullable : null};
										const newValue = nullableOf(newItems);
										updateState(field, newValue);
									} catch (e) {
										// ignore changes while json is not valid json
									}
								}
							}}
							syntax={json()}
							linter={jsonParseLinter()}
							formatter={value => JSON.stringify(JSON.parse(value), null, 2)}
						/>
						<DragHandle/>
					</Stack>}
			</DragAndDrop>

			<div>
				<IconButton
					color={"primary"}
					onClick={() => {
						const newItems = [...items, {display: "", value: "", extraDisplay: null}];
						const newValue = {isPresent: true, value: newItems};
						updateState(field, newValue);
					}}
					disabled={disabled}
				>
					<Add/>
				</IconButton>
			</div>
		</Stack>
	</Row>;
}

export function StringListField<TObject extends FormSetup | QuestionSetup>(
	data: DynamicFormsSetupEditorData<TObject>,
	field: keyof TObject & string,
	updateState: (field: string, value: any) => void,
	{
		isWrapped = false,
		disabled = false,
	}: {
		isWrapped?: boolean;
		disabled?: boolean;
	} = {}
) {
	const setup = data.setup;

	const items: string[] = getValue(field, data, isWrapped, []);

	return <Row key={field}>
		{isWrapped
			? <Checkbox
				checked={!!setup[field]}
				disabled={disabled}
				onChange={event => {
					if (event.target.checked) {
						updateState(field, getDefaultValue(field, data, isWrapped, []));
					} else {
						updateState(field, null);
					}
				}}
			/>
			: <Checkbox style={{visibility: "hidden"}}/>}

		<Stack style={{flexGrow: 1, alignItems: "stretch"}}>
			{translate(field)}

			<DragAndDrop items={items} disabled={disabled} onItemsChanged={newItems => {
				const newValue = isWrapped ? nullableOf(newItems) : newItems;
				updateState(field, newValue);
			}}>
				{(item, index) =>
					<Stack key={index.toString()} direction={"row"} alignItems={"center"}
						style={{width: "100%"}}>
						<IconButton
							color={"primary"}
							disabled={disabled}
							onClick={() => {
								const newItems = [...items];
								newItems.splice(index, 1);
								const newValue = isWrapped ? nullableOf(newItems) : newItems;
								updateState(field, newValue);
							}}
						>
							<Remove/>
						</IconButton>
						<TextField
							variant={"outlined"}
							value={item}
							style={{flexGrow: 1}}
							disabled={disabled}
							onChange={event => {
								const newItems = [...items];
								newItems[index] = event.target.value;
								const newValue = isWrapped ? nullableOf(newItems) : newItems;
								updateState(field, newValue);
							}}
						/>

						<DragHandle/>
					</Stack>}
			</DragAndDrop>

			<div>
				<IconButton
					color={"primary"}
					disabled={disabled}
					onClick={() => {
						const newItems = [...items, ""];
						const newValue = isWrapped ? nullableOf(newItems) : newItems;
						updateState(field, newValue);
					}}
				>
					<Add/>
				</IconButton>
			</div>
		</Stack>
	</Row>;
}

export function JsonListField<TObject extends FormSetup | QuestionSetup>(
	data: DynamicFormsSetupEditorData<TObject>,
	field: keyof TObject & string,
	updateState: (field: string, value: any) => void,
	{isWrapped = false, disabled = false}: { isWrapped?: boolean; disabled?: boolean } = {}
) {
	const setup = data.setup;

	const items: any[] = getValue(field, data, isWrapped, []);

	return <Row key={field}>
		{isWrapped
			? <Checkbox
				checked={!!setup[field]}
				onChange={event => {
					if (event.target.checked) {
						updateState(field, getDefaultValue(field, data, isWrapped, []));
					} else {
						updateState(field, null);
					}
				}}
				disabled={disabled}
			/>
			: <Checkbox style={{visibility: "hidden"}}/>}

		<Stack style={{flexGrow: 1, alignItems: "stretch"}}>
			{translate(field)}

			<DragAndDrop
				items={items}
				disabled={disabled}
				onItemsChanged={newItems => {
					const newValue = isWrapped ? nullableOf(newItems) : newItems;
					updateState(field, newValue);
				}}
			>
				{(item, index) => {
					const code = JSON.stringify(item, (key, value) => value !== null ? value : undefined, 2);
					return <Stack key={index.toString()} direction={"row"} alignItems={"center"}
						style={{width: "100%"}}>
						<IconButton
							color={"primary"}
							disabled={disabled}
							onClick={() => {
								const newItems = [...items];
								newItems.splice(index, 1);
								updateState(field, newItems);
							}}
						>
							<Remove/>
						</IconButton>
						<CodeTextField
							maxHeight={"400px"}
							TextFieldProps={{
								variant: "outlined",
								value: code,
								style: {flexGrow: 1},
								placeholder: "{ }",
								disabled: disabled,
								onChange: event => {
									try {
										const newValueJson: string = (event as any).value;
										const newItems = [...items];
										newItems[index] = JSON.parse(newValueJson);
										updateState(field, newItems);
									} catch (e) {
										// ignore changes while json is not valid json
									}
								}
							}}
							syntax={json()}
							linter={jsonParseLinter()}
						/>

						<DragHandle/>
					</Stack>;
				}}
			</DragAndDrop>

			<div>
				<IconButton
					color={"primary"}
					disabled={disabled}
					onClick={() => {
						const newItems = [...items, ""];
						updateState(field, newItems);
					}}
				>
					<Add/>
				</IconButton>
			</div>
		</Stack>
	</Row>;
}

export function jsonField<TObject extends FormSetup | QuestionSetup>(
	data: DynamicFormsSetupEditorData<TObject>,
	field: keyof TObject & string,
	updateState: (field: string, value: any) => void,
	{
		isWrapped = false,
		placeholder = "{ }",
		maxHeight,
		disabled = false,
	}: { isWrapped?: boolean; placeholder?: string; maxHeight?: string; disabled?: boolean } = {}
) {
	const setup = data.setup;
	const value = getValue(field, data, isWrapped);
	const code = JSON.stringify(value, null, 2);
	if (isWrapped) {
		return <Row key={field}>
			<Checkbox
				checked={!!setup[field]}
				disabled={disabled}
				onChange={event => {
					if (event.target.checked) {
						updateState(field, getDefaultValue(field, data, true));
					} else {
						updateState(field, null);
					}
				}}
			/>
			<CodeTextField
				maxHeight={maxHeight}
				TextFieldProps={{
					label: translate(field),
					variant: "outlined",
					value: code ?? "",
					style: {flexGrow: 1},
					placeholder: placeholder,
					disabled: disabled,
					onChange: event => {
						try {
							const newValueJson: string = (event as any).value;
							const newValue = nullableOf(JSON.parse(newValueJson));
							updateState(field, newValue);
						} catch (e) {
							// ignore changes while json is not valid json
						}
					}
				}}
				syntax={json()}
				linter={jsonParseLinter()}
				formatter={value => JSON.stringify(JSON.parse(value), null, 2)}
			/>
		</Row>;
	} else {
		return <Row key={field}>
			<Checkbox style={{visibility: "hidden"}}/>
			<CodeTextField
				maxHeight={maxHeight}
				TextFieldProps={{
					label: translate(field),
					variant: "outlined",
					value: code ?? "",
					style: {flexGrow: 1},
					placeholder: placeholder,
					disabled: disabled,
					onChange: event => {
						try {
							const newValueJson: string = (event as any).value;
							const newValue = JSON.parse(newValueJson);
							updateState(field, newValue);
						} catch (e) {
							// ignore changes while json is not valid json
						}
					}
				}}
				syntax={json()}
				linter={jsonParseLinter()}
			/>
		</Row>;
	}
}
